import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloError } from 'apollo-client';
import {
  getAccountRegion,
  getAccountRegionFromClaims,
} from 'microsites/partner-linking/account-region';
import {
  clearAppIdToken,
  getAppIdTokenFromSessionStorage,
  getAppTokenUserId,
  isExpiredJwt,
  isWellFormedJwt,
  readAppTokenFromUrl,
  storeAppToken,
  stripTokenFromUrl,
} from 'microsites/partner-linking/app-token';
import {
  getEmailVerified,
  getEmailVerifiedFromClaims,
} from 'microsites/partner-linking/email-verification';
import LinkingLoading from 'microsites/partner-linking/linking-loading';
import PartnerLinkingAuthGate from 'microsites/partner-linking/partner-linking-auth-gate';
import { generatePkceChallenge, storePkce } from 'microsites/partner-linking/pkce';
import { useBackToApp } from 'microsites/partner-linking/use-back-to-app';
import { useInitiateLinking } from 'microsites/partner-linking/use-initiate-linking';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Loggable } from '@rbi-ctg/frontend';
import { PartnerLinkSource } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { useLocale } from 'state/intl';
import { REGIONS } from 'state/intl/types';
import { LaunchDarklyFlag, useLDContext } from 'state/launchdarkly';
import { StatusType } from 'utils/datadog';
import { parseJwt } from 'utils/jwt';
import { LDUser } from 'utils/launchdarkly';
import { routes } from 'utils/routing';

import {
  PartnerLinkingBackendErrorCode,
  PartnerLinkingErrorReason,
  isEmailVerificationRequiredError,
  isRegionNotEligibleError,
} from '../errors';

import { makeCtLinkingLogger } from './log-linking';
import RateLimited from './rate-limited';
import RegionUnavailable from './region-unavailable';
import VerificationRequired from './verification-required';

const logInitiateLinking = makeCtLinkingLogger('initiate-linking');

// The gateway persists CT's own THIAPP / THIWEB values verbatim (THLE-4194), so
// pass them straight through. Anything else, including absent, means THIWEB.
function resolveSource(raw: string | null): PartnerLinkSource {
  if (raw === 'THIAPP') {
    return PartnerLinkSource.THIAPP;
  }
  if (raw === 'THIWEB') {
    return PartnerLinkSource.THIWEB;
  }
  return PartnerLinkSource.THIWEB;
}

// A deliberate backend short-circuit (RFC §Initiate Linking): the gateway throws
// `ALREADY_LINKED` so we can resolve the outcome without a second round-trip.
function isAlreadyLinkedError(err: unknown): boolean {
  if (!(err instanceof ApolloError)) {
    return false;
  }
  return (
    err.graphQLErrors?.some(
      e => e.extensions?.code === PartnerLinkingBackendErrorCode.AlreadyLinked
    ) ?? false
  );
}

// THLE-4029 — the backend caps LINKED events at 2 per user per 24h and throws
// `RATE_LIMITED` before any OIDC redirect.
function isRateLimitedError(err: unknown): boolean {
  if (!(err instanceof ApolloError)) {
    return false;
  }
  return (
    err.graphQLErrors?.some(
      e => e.extensions?.code === PartnerLinkingBackendErrorCode.RateLimited
    ) ?? false
  );
}

type View = 'loading' | 'rateLimited' | 'regionUnavailable' | 'verificationRequired';

// Region wins when both trip: a guest cannot fix their region from here, but
// can fix verification.
function initialView(isRegionBlocked: boolean, isVerificationBlocked: boolean): View {
  if (isRegionBlocked) {
    return 'regionUnavailable';
  }
  if (isVerificationBlocked) {
    return 'verificationRequired';
  }
  return 'loading';
}

// Identifies the guest so the CT-linking flag is evaluated for them rather than
// against the anonymous default. `key` is the cognitoId — target on it in
// LaunchDarkly.
function buildLinkingLdAttributes(
  appToken: string | null,
  user: UserDetails | null
): LDUser | null {
  if (appToken !== null) {
    const claims = parseJwt(appToken);
    const key = claims?.sub;
    if (!key) {
      return null;
    }
    return { key, email: claims?.email, anonymous: false, custom: { cognitoId: key } };
  }
  if (user?.cognitoId) {
    return {
      key: user.cognitoId,
      email: user.details?.email,
      name: user.details?.name,
      anonymous: false,
      custom: { cognitoId: user.cognitoId },
    };
  }
  return null;
}

function CanadianTireRedirect() {
  const { initiateLinking } = useInitiateLinking();
  const { attemptGetUpdatedLdFlag } = useLDContext();
  const { user, currentUserSession } = useAuthContext();
  const { language, region } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = resolveSource(searchParams.get('source'));
  // The flow always ends in a redirect/navigate, so guard against re-running it
  // if an unstable dependency (e.g. the LD context callback) re-fires the effect.
  const hasStartedRef = useRef(false);

  // The TH app passes its ID token in the URL hash. Read it once on mount,
  // persist it so it survives the OIDC round-trip, and strip it from the URL so
  // the raw token isn't left in the address bar. Falls back to an already
  // persisted one on a remount in the same tab.
  const [appToken] = useState<string | null>(() => {
    const fromUrl = readAppTokenFromUrl();
    if (fromUrl) {
      storeAppToken(fromUrl);
      stripTokenFromUrl();
      return fromUrl;
    }
    return getAppIdTokenFromSessionStorage();
  });

  // Only an explicit THIAPP entry deep-links back to the native app. Everything
  // else goes to TH web home, the safe default: a deep link would no-op without
  // the app installed.
  const isAppOrigin = searchParams.get('source') === 'THIAPP';
  const handleBackToApp = useBackToApp(isAppOrigin);

  // App-origin guests have no Cognito session, so the generic logger's `userId`
  // is empty for them. Recover the Cognito `sub` from the handoff token to keep
  // failures attributable. At component scope because the gates log from their
  // own effects, and memoized so none of them re-run on an ordinary render.
  const userId = getAppTokenUserId(appToken);
  const log = useCallback(
    (event: string, status: StatusType, context: Record<string, Loggable> = {}): void =>
      logInitiateLinking(event, status, userId ? { userId, ...context } : context),
    [userId]
  );

  // The identity every gate below judges, and the only one they may judge.
  //
  // A handoff token owns it whenever one is present, because that is what the
  // mutations authenticate as and the browser's session may be a different
  // guest. The session is read only in the token's absence, and only while
  // valid, since lapsed claims describe nobody this flow can act as.
  //
  // An expired token therefore yields no identity at all — falling back to the
  // session would link whichever account the browser happens to hold. The flow
  // effect ends the attempt instead.
  const identityToken = appToken !== null && !isExpiredJwt(appToken) ? appToken : null;
  const sessionClaims =
    appToken === null && currentUserSession?.isValid?.()
      ? currentUserSession.getIdToken().payload
      : null;

  // THLE-4256 — the CT partnership is Canada-only, so block ahead of the flag
  // read, `initiateLinking`, and the OIDC redirect. An unknown region fails open;
  // the backend's `REGION_NOT_ELIGIBLE` is authoritative.
  const accountRegion =
    identityToken !== null
      ? getAccountRegion(identityToken)
      : getAccountRegionFromClaims(sessionClaims);
  const isUsSiteRegion = region === REGIONS.US;
  const isUsLoyaltyAccount = accountRegion === 'US';
  const isRegionBlocked = isUsSiteRegion || isUsLoyaltyAccount;

  // THLE-4186 — same terms as the region gate. Failing open on an absent claim
  // keeps the backend's `EMAIL_VERIFICATION_REQUIRED` authoritative, and keeps a
  // guest with no identity out of this screen: they are asked to sign in instead.
  const emailVerified =
    identityToken !== null
      ? getEmailVerified(identityToken)
      : getEmailVerifiedFromClaims(sessionClaims);
  const isVerificationBlocked = !isRegionBlocked && emailVerified === false;

  // A block is terminal, so `view` latches it. Seeded eagerly to skip a frame of
  // the loading state.
  const [view, setView] = useState<View>(initialView(isRegionBlocked, isVerificationBlocked));

  // Same latch for the flow, which lives outside the render. A ref, not an
  // effect-cleanup flag: the flow effect's deps re-fire on ordinary re-renders,
  // which would cancel a healthy in-flight flow.
  const isBlockedRef = useRef(false);
  isBlockedRef.current = isBlockedRef.current || isRegionBlocked || isVerificationBlocked;

  useEffect(() => {
    if (!isRegionBlocked) {
      return;
    }
    // Own effect so this logs once per block, not on every flow-effect re-run.
    log('region blocked before initiateLinking', StatusType.info, {
      source,
      signal: isUsSiteRegion ? 'siteRegion' : 'loyaltyAccount',
    });
    // No `clearAppIdToken()` here, unlike the other terminal branches: a guest
    // who corrects their region reloads back into the flow, and without the
    // token the auth gate sends an app-origin guest to /signup.
    setView('regionUnavailable');
  }, [isRegionBlocked, isUsSiteRegion, source, log]);

  useEffect(() => {
    if (!isVerificationBlocked) {
      return;
    }
    // Own effect so this logs once per block, not on every flow-effect re-run.
    log('email unverified — blocked before initiateLinking', StatusType.info, { source });
    // No `clearAppIdToken()`, matching the region gate: without the token the
    // auth gate sends an app-origin guest to /signup on their retry.
    setView('verificationRequired');
  }, [isVerificationBlocked, source, log]);

  useEffect(() => {
    // Before the one-shot guard, so a blocked mount never marks the flow started.
    if (isBlockedRef.current) {
      return;
    }
    // Web-session flow: wait for the profile so the flag is evaluated for the
    // right user. The auth gate guarantees a session; the profile loads a beat
    // later and re-runs this effect.
    if (appToken === null && !user?.cognitoId) {
      return;
    }
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    (async () => {
      // A truncated or garbled hash can only fail downstream at the gateway with
      // the same generic error, so refuse before the round-trip.
      if (appToken !== null && !isWellFormedJwt(appToken)) {
        log('malformed app-handoff token', StatusType.error, { source });
        clearAppIdToken();
        navigate(routes.partnerLinkingError, {
          replace: true,
          state: {
            reason: PartnerLinkingErrorReason.InitiateMalformedToken,
            detail: 'malformed app-handoff token',
            isAppOrigin,
          },
        });
        return;
      }

      // Only reachable with a session of the browser's own, since the auth gate
      // turns an expired handoff away. Terminal rather than falling back to that
      // session, which may belong to a different guest than the app handed off.
      if (appToken !== null && isExpiredJwt(appToken)) {
        log('expired app-handoff token', StatusType.error, { source });
        clearAppIdToken();
        navigate(routes.partnerLinkingError, {
          replace: true,
          state: {
            reason: PartnerLinkingErrorReason.InitiateExpiredToken,
            detail: 'expired app-handoff token',
            isAppOrigin,
          },
        });
        return;
      }

      // Off is the default for anyone not explicitly targeted in LaunchDarkly, so
      // most guests land on the generic error screen. Falls back to off when no
      // identity can be built.
      const ldAttributes = buildLinkingLdAttributes(appToken, user);
      const linkingEnabled = ldAttributes
        ? await attemptGetUpdatedLdFlag(
            LaunchDarklyFlag.ENABLE_CT_PARTNERSHIP_LINKING,
            ldAttributes,
            false
          )
        : false;
      // The flag read is a network round-trip, so re-check on the way out: the
      // disabled branch below would navigate off a block screen and clear the
      // token that screen keeps for the retry.
      if (isBlockedRef.current) {
        log('eligibility gate closed during flag read', StatusType.info, { source });
        return;
      }
      if (!linkingEnabled) {
        logInitiateLinking('linking disabled for user — flag off', StatusType.info, { source });
        clearAppIdToken();
        navigate(routes.partnerLinkingError, {
          replace: true,
          state: {
            reason: PartnerLinkingErrorReason.LinkingDisabled,
            isAppOrigin,
          },
        });
        return;
      }

      try {
        const { oidcAuthUrl } = await initiateLinking({ partnerId: 'CT', source, appToken });
        // PKCE and `state` are RP-Site-owned (RFC 6749 §10.12, RFC 7636): the
        // verifier never leaves this browser, and `state` is checked on the
        // callback. Both are persisted by `state` so the callback can recover them.
        const { codeVerifier, codeChallenge, state } = await generatePkceChallenge();
        // Should be unreachable, but a stale redirect would drop a guest looking
        // at a block screen into the partner's OIDC flow. After the last `await`
        // so neither suspension point is uncovered, and before `storePkce` so a
        // blocked flow leaves no orphan entry.
        if (isBlockedRef.current) {
          log('eligibility gate closed before OIDC redirect', StatusType.info, { source });
          return;
        }
        storePkce(state, { codeVerifier, source });
        const url = new URL(oidcAuthUrl);
        url.searchParams.set('code_challenge', codeChallenge);
        url.searchParams.set('code_challenge_method', 'S256');
        url.searchParams.set('state', state);
        // OIDC core param: renders the partner's consent and error screens in the
        // guest's locale.
        url.searchParams.set('ui_locales', language);
        log('redirecting to partner OIDC', StatusType.info, { source });
        window.location.href = url.toString();
      } catch (err) {
        // Failure path for the same awaits: most branches navigate off the block
        // screen, and all but the verification refusal drop the handoff token.
        if (isBlockedRef.current) {
          log('eligibility gate closed during initiateLinking', StatusType.info, { source });
          return;
        }
        if (isEmailVerificationRequiredError(err)) {
          // Terminal, with no INITIATED record created. Keep the handoff token,
          // matching the client gate — without it the auth gate sends an
          // app-origin guest to /signup when they reload to retry.
          log('email unverified — refused by initiateLinking', StatusType.info, { source });
          setView('verificationRequired');
          return;
        }
        // The remaining paths end the flow, so drop the handoff token.
        clearAppIdToken();
        if (isAlreadyLinkedError(err)) {
          // A success outcome, so it lands on the same Triangle success page a
          // fresh `completeLinking` does — not a bespoke "already linked" screen.
          log('already linked — backend short-circuit', StatusType.info, { source });
          navigate(
            language === 'fr'
              ? routes.partnerLinkingCanadianTireSuccessFr
              : routes.partnerLinkingCanadianTireSuccess,
            { replace: true }
          );
          return;
        }
        if (isRateLimitedError(err)) {
          log('rate limited — link cap reached', StatusType.warn, { source });
          setView('rateLimited');
          return;
        }
        if (isRegionNotEligibleError(err)) {
          log('region blocked by initiateLinking', StatusType.info, { source });
          setView('regionUnavailable');
          return;
        }
        // `error.tsx` logs `reason` and `detail` on mount; this adds the full
        // error context for the funnel.
        log('initiateLinking call failed', StatusType.error, {
          source,
          errorMessage: err instanceof Error ? err.message : String(err),
        });
        navigate(routes.partnerLinkingError, {
          replace: true,
          state: {
            reason: PartnerLinkingErrorReason.InitiateFailed,
            detail: err instanceof Error ? err.message : String(err),
            isAppOrigin,
          },
        });
      }
    })();
  }, [
    initiateLinking,
    source,
    language,
    navigate,
    isAppOrigin,
    appToken,
    user,
    attemptGetUpdatedLdFlag,
    log,
  ]);

  if (view === 'regionUnavailable') {
    return <RegionUnavailable onBackToApp={handleBackToApp} />;
  }

  if (view === 'verificationRequired') {
    // Same terms as the gate above: the token owns the identity when there is
    // one, and an expired one is not an identity to send as.
    return <VerificationRequired onBackToApp={handleBackToApp} idToken={identityToken} />;
  }

  if (view === 'rateLimited') {
    return <RateLimited onBackToApp={handleBackToApp} />;
  }

  return <LinkingLoading />;
}

export default function CanadianTireInitiateLinking() {
  return (
    <PartnerLinkingAuthGate keepParams>
      <CanadianTireRedirect />
    </PartnerLinkingAuthGate>
  );
}
